import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import styles from './CookieConsent.module.css'

const COOKIE_CONSENT_KEY = 'cookie_consent'
const COOKIE_CONSENT_VERSION = '1.0'

type ConsentStatus = 'pending' | 'accepted' | 'rejected'

interface ConsentData {
  status: ConsentStatus
  version: string
  timestamp: string
  preferences: {
    necessary: boolean
    analytics: boolean
    marketing: boolean
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: true,
    marketing: false
  })

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (stored) {
      try {
        const data: ConsentData = JSON.parse(stored)
        // Show banner again if version changed
        if (data.version !== COOKIE_CONSENT_VERSION) {
          setVisible(true)
        }
      } catch {
        setVisible(true)
      }
    } else {
      // Small delay before showing to not be intrusive immediately
      const timer = setTimeout(() => setVisible(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const saveConsent = (status: ConsentStatus) => {
    const data: ConsentData = {
      status,
      version: COOKIE_CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      preferences: status === 'accepted' ? preferences : {
        necessary: true,
        analytics: false,
        marketing: false
      }
    }
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(data))
    setVisible(false)
  }

  const handleAcceptAll = () => {
    setPreferences({ necessary: true, analytics: true, marketing: true })
    saveConsent('accepted')
  }

  const handleAcceptSelected = () => {
    saveConsent('accepted')
  }

  const handleRejectAll = () => {
    saveConsent('rejected')
  }

  /**
   * Publish how much room this banner takes at the bottom of the screen.
   *
   * Anything else anchored down there reads this and sits above it. Without
   * it the banner covered the update prompt's own Reload and Later buttons,
   * so a user was told a new version was waiting and had no way to take it.
   * Observed rather than measured once, because the banner grows when
   * "Customize" opens the preference list.
   */
  useEffect(() => {
    const el = overlayRef.current
    const root = document.documentElement
    if (!visible || !el) {
      root.style.removeProperty('--consent-height')
      return
    }
    const publish = () => root.style.setProperty('--consent-height', el.offsetHeight + 'px')
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--consent-height')
    }
  }, [visible])

  if (!visible) return null

  return (
    <div className={styles.overlay} ref={overlayRef}>
      <div className={styles.banner} role="dialog" aria-labelledby="cookie-title">
        <div className={styles.content}>
          <h3 id="cookie-title" className={styles.title}>
            Cookies
          </h3>
          <p className={styles.description}>
            Necessary cookies keep the site working. Analytics cookies tell us which pages
            get used. Choose what to allow. <Link to="/privacy" className={styles.link}>Privacy policy</Link>.
          </p>

          {showPreferences && (
            <div className={styles.preferences}>
              <label className={styles.preferenceItem}>
                <input
                  type="checkbox"
                  checked={preferences.necessary}
                  disabled
                  className={styles.checkbox}
                />
                <div>
                  <span className={styles.preferenceLabel}>Necessary</span>
                  <span className={styles.preferenceDesc}>Required for the website to function</span>
                </div>
              </label>

              <label className={styles.preferenceItem}>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                  className={styles.checkbox}
                />
                <div>
                  <span className={styles.preferenceLabel}>Analytics</span>
                  <span className={styles.preferenceDesc}>Help us improve by tracking usage</span>
                </div>
              </label>

              <label className={styles.preferenceItem}>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                  className={styles.checkbox}
                />
                <div>
                  <span className={styles.preferenceLabel}>Marketing</span>
                  <span className={styles.preferenceDesc}>Personalized ads and offers</span>
                </div>
              </label>
            </div>
          )}

          <div className={styles.actions}>
            {!showPreferences ? (
              <>
                <button
                  onClick={() => setShowPreferences(true)}
                  className={styles.buttonSecondary}
                >
                  Customize
                </button>
                <button
                  onClick={handleRejectAll}
                  className={styles.buttonSecondary}
                >
                  Reject All
                </button>
                <button
                  onClick={handleAcceptAll}
                  className={styles.buttonPrimary}
                >
                  Accept All
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowPreferences(false)}
                  className={styles.buttonSecondary}
                >
                  Back
                </button>
                <button
                  onClick={handleAcceptSelected}
                  className={styles.buttonPrimary}
                >
                  Save Preferences
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Hook to check cookie consent status
 */
export function useCookieConsent() {
  const [consent, setConsent] = useState<ConsentData | null>(null)

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (stored) {
      try {
        setConsent(JSON.parse(stored))
      } catch {
        setConsent(null)
      }
    }
  }, [])

  return {
    hasConsent: consent?.status === 'accepted',
    analyticsAllowed: consent?.preferences?.analytics ?? false,
    marketingAllowed: consent?.preferences?.marketing ?? false,
    consent
  }
}
